import React, {useState, useEffect, useCallback, useRef} from 'react';
import {withTranslation} from 'react-i18next';
import {Column} from 'primereact/column';
import {DataTable} from 'primereact/datatable';
import axios from 'axios';
import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {InputNumber} from 'primereact/inputnumber';
import {Message} from 'primereact/message';
import InventoryReceiptMatchGraph from './InventoryReceiptMatchGraph';
import {fetchAllMatchItems, itemLinks} from './inventoryReceiptMatchUtils';

const apiCall = (method, url, data) => axios({
  method, url: process.env.REACT_APP_API_HOST + url, data,
  headers: {Authorization: 'Bearer ' + localStorage.getItem('userToken'), 'App-Network': localStorage.getItem('networkId')}
});

const PurchaseorderInventorydocumentCopyDndComponent = props => {
  const {t} = props;
  const receiptId = window.App.urlVars()?.receiptId;
  const inventoryDocumentId = window.App.urlVars()?.inventoryDocumentId;
  const [requests, setRequests] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [loadingDropTransfer, setLoadingDropTransfer] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [matchDialog, setMatchDialog] = useState(null);
  const [matchError, setMatchError] = useState('');
  const generation = useRef(0);
  const links = itemLinks;
  const linkedQuantity = item => links(item).reduce((sum, link) => sum + Number(link.quantity || 0), 0);
  const formatQuantity = value => new Intl.NumberFormat(props.i18n?.language || 'hu', {maximumFractionDigits: 3}).format(value);
  const refresh = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    setLoadError('');
    try {
      const [invoiceRows, inventoryRows] = await Promise.all([
        receiptId ? fetchAllMatchItems(url => apiCall('GET', url), `/api/v2/receipt-item?search[receipt][operator]==&search[receipt][value]=${encodeURIComponent(receiptId)}`) : [],
        inventoryDocumentId ? fetchAllMatchItems(url => apiCall('GET', url), `/api/v2/inventory-document-item?search[inventory_document][operator]==&search[inventory_document][value]=${encodeURIComponent(inventoryDocumentId)}`) : []
      ]);
      if (current !== generation.current) return;
      setRequests(invoiceRows);
      setItems(inventoryRows);
      setSelectedRequests([]);
    } catch (error) {
      if (current === generation.current) {
        setLoadError(t('itemMatchLoadError'));
        setRequests([]);
        setItems([]);
      }
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [receiptId, inventoryDocumentId, t]);
  const rerender = window.App.state?.rerenderPurchaseorderInventorydocumentCopyDnd;
  const rerenderRequests = window.App.state?.rerenderPurchaserequestItems;
  const rerenderItems = window.App.state?.rerenderPurchaseorderItems;
  useEffect(() => {
    refresh();
    return () => { generation.current++; };
  }, [refresh, rerender, rerenderRequests, rerenderItems]);

  const handleDeleteInventoryDocumentItem = async (inventoryId, invoiceId) => {
    setLoadingDropTransfer(true);
    try {
      await apiCall('POST', `/api/v2/inventory-document-item/transfer-supplier/${inventoryId}/matches`, {
        matches: [{receipt_item_id: invoiceId, quantity: 0}]
      });
      await refresh();
    } catch (error) {
      window.App.toastShow('error', t('error'), error.response?.data?.message || error.toString());
    } finally { setLoadingDropTransfer(false); }
  };

  const openMatchDialog = (target, selectedIds = []) => {
    if (!target) return;
    let free = Math.max(0, Number(target.quantity) - Number(target.matched_quantity || 0));
    const rows = requests.map(row => {
      const current = links(row).filter(link => link.id === target.id).reduce((sum, link) => sum + Number(link.quantity), 0);
      const available = Math.max(0, Number(row.quantity) - linkedQuantity(row));
      const added = selectedIds.includes(row.id) ? Math.min(free, available) : 0;
      free -= added;
      return {id: row.id, description: row.description, unit: row.unit?.name, original: current, quantity: current + added, max: current + available};
    });
    setMatchError('');
    setMatchDialog({target, rows});
  };
  const matchTotal = matchDialog?.rows.reduce((sum, row) => sum + Number(row.quantity || 0), 0) || 0;
  const matchOriginal = matchDialog?.rows.reduce((sum, row) => sum + row.original, 0) || 0;
  const matchCapacity = matchDialog ? Number(matchDialog.target.quantity) - Number(matchDialog.target.matched_quantity || 0) + matchOriginal : 0;
  const saveMatches = async () => {
    setLoadingDropTransfer(true);
    setMatchError('');
    try {
      await apiCall('post', `/api/v2/inventory-document-item/transfer-supplier/${matchDialog.target.id}/matches`, {
        matches: matchDialog.rows.filter(row => row.quantity !== row.original).map(row => ({receipt_item_id: row.id, quantity: Number(row.quantity || 0)})),
      });
      setMatchDialog(null);
      setSelectedRequests([]);
      await refresh();
    } catch (error) {
      setMatchError(error.response?.data?.message || t('error'));
    } finally { setLoadingDropTransfer(false); }
  };


  return <div className="purchaserequest-dnd widget">
      <Dialog header={t('itemMatchTitle')} visible={!!matchDialog} onHide={() => !loadingDropTransfer && setMatchDialog(null)} style={{width: '720px', maxWidth: '95vw'}}
        footer={<><Button label={t('cancel')} className="p-button-text" disabled={loadingDropTransfer} onClick={() => setMatchDialog(null)}/><Button label={t('save')} loading={loadingDropTransfer} disabled={matchTotal > matchCapacity + 0.000001} onClick={saveMatches}/></>}>
        {matchDialog && <>
          <h4>{matchDialog.target.material?.name}</h4>
          <p>{t('itemMatchProgress', {total: formatQuantity(matchDialog.target.quantity), matched: formatQuantity(Number(matchDialog.target.matched_quantity || 0) - matchOriginal + matchTotal), remaining: formatQuantity(matchCapacity - matchTotal), unit: matchDialog.target.unit?.name})}</p>
          <p>{t('itemMatchHelp')}</p>
          {matchError && <Message severity="error" text={matchError}/>}
          {matchTotal > matchCapacity + 0.000001 && <Message severity="warn" text={t('itemMatchOverCapacity')}/>}
          <DataTable value={matchDialog.rows} dataKey="id" className="p-datatable-sm" responsiveLayout="scroll">
            <Column field="description" header={t('columnName')}/>
            <Column header={t('itemMatchAvailable')} body={row => `${formatQuantity(row.max)} ${row.unit || ''}`}/>
            <Column header={t('itemMatchQuantity')} body={row => <InputNumber value={row.quantity} min={0} max={row.max} maxFractionDigits={3} locale={props.i18n?.language || 'hu'} disabled={loadingDropTransfer} inputStyle={{width: '130px'}} onValueChange={e => setMatchDialog(current => ({...current, rows: current.rows.map(r => r.id === row.id ? {...r, quantity: e.value ?? 0} : r)}))}/>}/>
          </DataTable>
        </>}
      </Dialog>

    {loadError && <div role="alert"><Message severity="error" text={loadError}/><Button label={t('itemMatchRetry')} onClick={refresh}/></div>}
    <InventoryReceiptMatchGraph items={items} requests={requests} t={t} formatQuantity={formatQuantity}
      busy={loading || loadingDropTransfer} selected={selectedRequests} onSelectionChange={setSelectedRequests}
      onEdit={openMatchDialog} onUnlink={handleDeleteInventoryDocumentItem}/>
  </div>;
};
export default withTranslation('common')(PurchaseorderInventorydocumentCopyDndComponent);
